/**
 * Fetch project item IDs and check ROI annotation coverage (GET .../annotation/roi per item).
 * 200 = ROI layer exists; 404 = missing (per project_annotations.get_item_roi_annotation).
 */

const DEFAULT_CONCURRENCY = 6;

/**
 * @param {*} api - girder API client (get)
 * @param {string} folderId
 * @returns {Promise<{ ok: true, itemIds: string[] } | { ok: false, error: string, itemIds: [] }>}
 */
export async function fetchProjectItemIds(api, folderId) {
    const r = await api.get(`projects/${folderId}/items`, {});
    if (r.status !== 200) {
        const msg = typeof r.data === 'object' && r.data?.message
            ? r.data.message
            : 'Failed to load project items.';
        return { ok: false, error: msg, itemIds: [] };
    }
    const items = r.data?.items ?? [];
    const itemIds = items.map((it) => String(it._id));
    return { ok: true, itemIds };
}

/**
 * @param {*} api - girder API client (get)
 * @param {string} folderId
 * @param {string} itemId
 * @returns {Promise<boolean>} true if ROI annotation exists
 */
export async function itemHasRoiAnnotation(api, folderId, itemId) {
    try {
        const r = await api.get(
            `projects/${folderId}/items/${itemId}/annotation/roi`,
            {}
        );
        return r.status === 200;
    } catch {
        return false;
    }
}

/**
 * @param {*} api - girder API client (get)
 * @param {string} folderId
 * @param {string[]} itemIds
 * @param {{ concurrency?: number, isCancelled?: () => boolean }} [options]
 * @returns {Promise<{ total: number, missingIds: string[], withRoiCount: number }>}
 */
export async function checkRoiCoverage(api, folderId, itemIds, options = {}) {
    if (!itemIds.length) {
        return { total: 0, missingIds: [], withRoiCount: 0 };
    }

    const concurrency = Math.max(
        1,
        Math.min(options.concurrency ?? DEFAULT_CONCURRENCY, itemIds.length)
    );
    const isCancelled = options.isCancelled ?? (() => false);

    const missingIds = [];
    let cursor = 0;

    async function worker() {
        for (;;) {
            if (isCancelled()) return;
            const i = cursor;
            cursor += 1;
            if (i >= itemIds.length) return;
            const id = itemIds[i];
            const has = await itemHasRoiAnnotation(api, folderId, id);
            if (isCancelled()) return;
            if (!has) missingIds.push(id);
        }
    }

    const workers = Array.from({ length: concurrency }, () => worker());
    await Promise.all(workers);

    return {
        total: itemIds.length,
        missingIds,
        withRoiCount: itemIds.length - missingIds.length,
    };
}
